import { AlertTriangle, Building2, Clock, ShieldCheck, Users } from "lucide-react";

import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";

export interface ClientsKpiStripProps {
  /** Picks the real total's label and icon: organizations portfolio (level 1) or clients list. */
  scope: "organizations" | "clients";
  /** Server total of the current query. */
  total: number;
  isLoading?: boolean;
}

/**
 * Executive stat strip above the table. Only the total is backed by the API today; the three KYC breakdown
 * cards stay as "coming soon" placeholders until the backend exposes portfolio stats (see
 * docs/clients-view-gaps.md) — values are never fabricated.
 */
export function ClientsKpiStrip({ scope, total, isLoading = false }: ClientsKpiStripProps) {
  const { t } = useTranslations("clients");

  const comingSoonValue = <span className="text-muted-foreground">—</span>;
  const comingSoonMeta = (
    <ColocaStatusPill variant="info" size="sm">
      {t("coming_soon")}
    </ColocaStatusPill>
  );

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <ColocaKpiCard
        label={scope === "organizations" ? t("kpi_total_organizations") : t("kpi_total_clients")}
        value={isLoading ? <ColocaSkeleton className="h-8 w-20" /> : total.toLocaleString()}
        icon={scope === "organizations" ? Building2 : Users}
        accent="primary"
        meta={t("kpi_total_meta")}
      />
      <ColocaKpiCard
        label={t("kpi_verified")}
        value={comingSoonValue}
        icon={ShieldCheck}
        accent="neutral"
        meta={comingSoonMeta}
      />
      <ColocaKpiCard
        label={t("kpi_pending_review")}
        value={comingSoonValue}
        icon={Clock}
        accent="neutral"
        meta={comingSoonMeta}
      />
      <ColocaKpiCard
        label={t("kpi_rejected_expired")}
        value={comingSoonValue}
        icon={AlertTriangle}
        accent="neutral"
        meta={comingSoonMeta}
      />
    </div>
  );
}
